import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { gql } from 'graphql-request';
import { MorningHuddleTypes, PracticeAnalyticsAggregations, PracticeAnalyticsApi } from '@frontend/api-analytics';
import { convertToDateId, DemoChip } from '@frontend/analytics-shared';
import { BarChartData, Chart, YAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { usePracticeAnalyticsShallowStore } from '../practice/hooks/use-practice-analytics-store';

type MonthProductionProps = {
  reportDate: string;
};

const query = gql`
  query ($lmtdStart: Int!, $lmtdEnd: Int!, $mtdStart: Int!, $mtdEnd: Int!, $monthEnd: Int!) {
    location {
      id
      lmtdProduction: production {
        totals(start: $lmtdStart, end: $lmtdEnd, step: MONTHLY) {
          completedProduction
        }
      }
      mtdProduction: production {
        totals(start: $mtdStart, end: $mtdEnd, step: DAILY) {
          completedProduction
        }
        projections(start: $mtdEnd, end: $monthEnd, step: DAILY) {
          totalScheduled
        }
      }
    }
  }
`;

export const MonthProduction: FC<React.PropsWithChildren<MonthProductionProps>> = ({ reportDate }) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

  const [productionData, setProductionData] = useState<
    { chartData: BarChartData; values: Record<string, number> } | undefined
  >();

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.morningHuddle(`monthProductionToDate-${isDemoAccount}-${reportDate}-${filters.locations}`),
    queryFn: () => {
      if (isDemoAccount || !filters.locations?.length) {
        return null;
      }

      const date = dayjs(reportDate);

      return PracticeAnalyticsApi.getPracticeAnalyticsRecords<MorningHuddleTypes.MonthProductionResponse>({
        locationIds: filters.locations,
        queries: [query],
        variables: {
          lmtdEnd: convertToDateId(date.subtract(1, 'months')),
          lmtdStart: convertToDateId(date.subtract(1, 'months').startOf('month')),
          monthEnd: convertToDateId(date.endOf('month')),
          mtdEnd: convertToDateId(date),
          mtdStart: convertToDateId(date.startOf('month')),
        },
      });
    },
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    retry: false,
    refetchOnWindowFocus: false,
    select: (data) => (isDemoAccount ? demoData?.morningHuddleProductionMTD : data),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const aggregatedData = PracticeAnalyticsAggregations.morningHuddleMonthProduction(data);

    if (!aggregatedData) {
      return;
    }

    const { lmtdProduction, mtdProduction } = aggregatedData;
    const completed = mtdProduction.totals.completedProduction;
    const remaining = mtdProduction.projections.totalScheduled;
    const estimated = completed + remaining;
    const lastMonth = lmtdProduction.totals.completedProduction;

    setProductionData({
      chartData: {
        groups: [
          {
            name: 'completed',
            values: {
              completed: completed || 0,
            },
          },
          {
            name: 'remaining',
            values: {
              remaining: remaining || 0,
            },
          },
          {
            name: 'estimated',
            values: {
              estimated: estimated || 0,
            },
          },
        ],
        markers: [
          {
            dottedLine: true,
            formatter: (value) => formatters.currency(value),
            id: 'lastMonth',
            label: t('Last Month'),
            value: lastMonth,
          },
        ],
      },
      values: {
        completed,
        estimated,
        lastMonth,
        remaining,
      },
    });
  }, [data]);

  return (
    <Chart
      colors={{
        completed: theme.colors.primary40,
        estimated: theme.colors.neutral20,
        lastMonth: theme.colors.success30,
        remaining: theme.colors.primary20,
      }}
      css={styles.chartStyles}
      isLoading={isLoading}
      labels={{
        completed: t('MTD Completed'),
        estimated: t('Estimated Production'),
        remaining: t('Remaining Production'),
      }}
    >
      <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Production Month to Date')} />
      <Chart.BarChart
        appearance={{
          customYAxisTick: ({ labels, groupName, ...rest }) => (
            <YAxisLabelValueTick
              {...rest}
              label={labels?.[groupName] || groupName}
              onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
              useCustomTransitionsInSmallMode
              value={
                isLargeChart
                  ? formatters.currency(productionData?.values[groupName])
                  : formatters.shortenCurrency(productionData?.values[groupName])
              }
              xTransitionLabel={isLargeChart ? -230 : -160}
            />
          ),
          groupsGap: 24,
          hideTooltip: true,
          layout: 'vertical',
          margin: { top: 20, right: 80, bottom: 0, left: isLargeChart ? 180 : 110 },
          showYAxis: true,
          showGridLines: true,
        }}
        data={productionData?.chartData}
      />
    </Chart>
  );
};

const styles = {
  chartStyles: css`
    flex-basis: calc(70% - ${theme.spacing(6)});
    flex-grow: 1;
    margin-bottom: 0;
    min-width: 400px;
  `,
};
